import { HTTP_HL7_ORG, HTTP_TERMINOLOGY_HL7_ORG } from '@medplum/core';

//------------------------------------------------------------------------------
export const SYSTEMS = {
  // external standard systems
  SNOMED: 'http://snomed.info/sct',
  LOINC: 'http://loinc.org',
  UCUM: 'http://unitsofmeasure.org',
  HPO: 'http://human-phenotype-ontology.org',
  OMIM: 'https://omim.org/',
  MONDO: 'https://mondo.monarchinitiative.org/',
  ORPHA: 'http://www.orpha.net/',
};

//------------------------------------------------------------------------------
export const PREFIXES = {
  SNOMED: 'SNOMEDCT_US:',
  MONDO: 'MONDO:',
  OMIM: 'OMIM:',
};

//------------------------------------------------------------------------------
const HL7_BASE = HTTP_HL7_ORG;
const HL7_TERMINOLOGY = HTTP_TERMINOLOGY_HL7_ORG;

export const HL7 = {
  ORG: {
    BASE: HL7_BASE,
    SD: {
      ALLERGY_INTOLERANCE: `${HL7_BASE}/fhir/us/core/StructureDefinition/us-core-allergyintolerance`,
      CONDITION_PROBLEM: `${HL7_BASE}/fhir/us/core/StructureDefinition/us-core-condition-problems-health-concerns`,
      PROCEDURE: `${HL7_BASE}/fhir/us/core/StructureDefinition/us-core-procedure`,
      SMOKING_STATUS: `${HL7_BASE}/fhir/us/core/StructureDefinition/us-core-smokingstatus`,
      SIMPLE_OBSERVATION: `${HL7_BASE}/fhir/us/core/StructureDefinition/us-core-simple-observation`,
      QUESTIONNAIRE_ITEM_CONTROL: `${HL7_BASE}/fhir/StructureDefinition/questionnaire-itemControl`,
      QUESTIONNAIRE_ITEM_UNIT: `${HL7_BASE}/fhir/StructureDefinition/questionnaire-unit`,
    },
    VS: {
      ENCOUNTER: {
        STATUS: `${HL7_BASE}/fhir/ValueSet/encounter-status`,
        TYPE: `${HL7_BASE}/fhir/ValueSet/encounter-type`,
      },
      TASK: {
        STATUS: `${HL7_BASE}/fhir/ValueSet/task-status`,
      },
    },
  },
  TERMINOLOGY: {
    BASE: HL7_TERMINOLOGY,
    CS: {
      V3: {
        ACT_CODE: `${HL7_TERMINOLOGY}/CodeSystem/v3-ActCode`,
        DOCUMENT_TYPE: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/v3-DocumentType`,
          CODES: {
            PATIENT_CANVAS: 'patient-canvas',
          },
        },
        ROLE_CODE: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/v3-RoleCode`,
          CODES: {
            POWER_OF_ATTORNEY: 'POWATT',
            MOTHER: 'MTH',
            FATHER: 'FTH',
            GUARDIAN: 'GUARD',
            GRANDPARENT: 'GRPRN',
            SIBLING: 'SIB',
            SPOUSE: 'SPS',
          },
        },
        PARTICIPATION_TYPE: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/v3-ParticipationType`,
          CODES: {
            REFERRER: 'REF',
            ATND: 'ATND',
            CON: 'CON',
          },
        },
        OBSERVATION_INTERPRETATION: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/v3-ObservationInterpretation`,
          CODES: {
            NORMAL: 'N',
          },
        },
      },
      RISK_ASSESSMENT: {
        REVIEW: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/risk-assessment`,
          CODES: {
            REVIEW: 'risk-assessment-review',
            REMINDER: 'risk-assessment-review-reminder',
          },
        },
        METHOD: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/risk-assessment-method`,
          CODES: {
            ML_PREDICTION: 'ASTM-E2552',
          },
        },
      },
      CONDITION: {
        CATEGORY: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/condition-category`,
          CODES: {
            PROBLEM_LIST_ITEM: 'problem-list-item',
            ENCOUNTER_DIAGNOSIS: 'encounter-diagnosis',
            GENETIC_DISEASE: 'genetic-disease',
            MEDICAL_HISTORY: 'medical-history',
          },
        },
        CLINICAL: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/condition-clinical`,
          CODES: {
            ACTIVE: 'active',
            INACTIVE: 'inactive',
            RESOLVED: 'resolved',
          },
        },
        VERIFICATION_STATUS: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/condition-ver-status`,
          CODES: {
            CONFIRMED: 'confirmed',
          },
        },
      },
      OBSERVATION: {
        CATEGORY: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/observation-category`,
        },
      },
      COMMUNICATION: {
        CATEGORY: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/communication-category`,
          CODES: {
            INSTRUCTION: 'instruction',
            NOTIFICATION: 'notification',
            ALERT: 'alert',
          },
        },
        METHOD: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/communication-method`,
          CODES: {
            EMAIL: 'email',
            PHONE: 'phone',
            SMS: 'sms',
          },
        },
        TOPIC: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/communication-topic`,
          CODES: {
            APPOINTMENT: 'appointment',
            TEST_RESULTS: 'test-results',
            GENERAL: 'general',
          },
        },
        STATUS: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/communication-status`,
          CODES: {
            COMPLETED: 'completed',
            IN_PROGRESS: 'in-progress',
            FAILED: 'failed',
          },
        },
      },
      CONSENT: {
        SCOPE: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/consentscope`,
          CODES: {
            RESEARCH: 'research',
          },
        },
        CATEGORY: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/consentcategorycodes`,
          CODES: {
            RESEARCH: 'research',
          },
        },
        POLICY_CODES: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/consentpolicycodes`,
          CODES: {
            CRIC: 'cric',
          },
        },
      },
      TASK: {
        INPUT_TYPE: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/task-input-type`,
          CODES: {
            REFERENCE: 'reference',
            STRING: 'string',
            TARGET_URL: 'target-url',
          },
        },
        OUTPUT_TYPE: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/task-output-type`,
          CODES: {
            REVIEW_DECISION: 'review-decision',
            REVIEW_COMMENTS: 'review-comments',
            REVIEW_TIMESTAMP: 'review-timestamp',
          },
        },
      },
      ALLERGY_INTOLERANCE: {
        CLINICAL: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/allergyintolerance-clinical`,
          CODES: {
            ACTIVE: 'active',
            INACTIVE: 'inactive',
            RESOLVED: 'resolved',
          },
        },
        VERIFICATION: {
          BASE: `${HL7_TERMINOLOGY}/CodeSystem/allergyintolerance-verification`,
          CODES: {
            ENTERED_IN_ERROR: 'entered-in-error',
          },
        },
      },
    },
    VS: {
      V3: {
        ENCOUNTER_CODE: `${HL7_TERMINOLOGY}/ValueSet/v3-ActEncounterCode`,
      },
    },
  },
};

//------------------------------------------------------------------------------
const ARKON_BASE = 'http://www.arkon.health';

export const ARKON = {
  BASE: ARKON_BASE,
  CS: {
    ICD10: {
      BASE: `${ARKON_BASE}/fhir/CodeSystem/cs-icd-10-el`,
    },
    NOTES: {
      BASE: `${ARKON_BASE}/fhir/CodeSystem/note-category`,
    },
    PATIENT_NOTES: {
      BASE: `${ARKON_BASE}/fhir/CodeSystem/patient-notes`,
      CODES: {
        CLINICAL_NOTE: 'clinical-note',
      },
    },
    PHYSICAL_EXAMINATION: {
      BASE: `${ARKON_BASE}/fhir/CodeSystem/cs-physical-examination`,
      CODES: {
        HEAD_NECK: 'head-neck-examination',
        THORAX: 'thorax-examination',
        ABDOMEN: 'abdomen-examination',
        EXTREMITIES: 'extremities-examination',
        NEUROLOGICAL: 'neurological-examination',
      },
    },
    CONDITIONS: {
      FIRST_SYMPTOM: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/condition-first-symptom`,
      },
      SYMPTOM_ONSET_AGE: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/condition-symptom-onset-age`,
      },
      CLINICAL: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/cs-condition-clinical-el`,
        CODES: {
          ACTIVE: 'active',
        },
      },
    },
    PRACTITIONER: {
      ROLE: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/practitioner-role`,
        CODES: {
          ASSIGNED: 'assigned',
          REFERRAL: 'referral',
        },
      },
      ORGANIZATION: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/practitioner-organization`,
        CODES: {
          INTERNAL: 'internal',
        },
      },
    },
    ENCOUNTER: {
      TYPE: {
        CODES: {
          LABORATORY_CHECK: 'laboratory-check',
        },
      },
    },
    FOLLOW_UP_TASK: {
      BASE: `${ARKON_BASE}/fhir/CodeSystem/follow-up-task-type`,
      CODES: {
        INFORM_REFERRAL_DOCTOR: 'inform-referral-doctor',
        CONTACT_PATIENT: 'contact-patient',
      },
    },
    SPECIMEN: {
      TYPE: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/specimen-type`,
      },
      QUALITY: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/specimen-quality`,
      },
      CONDITION: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/specimen-condition`,
        CODES: {
          SATISFACTORY: 'satisfactory',
          UNSATISFACTORY: 'unsatisfactory',
        },
      },
      IDS: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/specimen-ids`,
      },
      BARCODE: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/specimen-barcode`,
      },
    },

    GENETIC_TESTS: {
      BASE: `${ARKON_BASE}/fhir/CodeSystem/cs-genetic-tests-el`,
    },

    THERAPEUTIC_INTERVENTION: {
      BASE: `${ARKON_BASE}/fhir/CodeSystem/cs-therapeutic-intervention-el`,
    },

    SERVICE_REQUEST: {
      STATUS: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/lab-order-status`,
      },
      IDS: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/lab-order-ids`,
      },
      REFERRAL_REASON: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/lab-order-referral-reasons`,
      },
      AUTHORIZED_PERSON: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/authorized-person`,
      },
    },

    // Payment tracking
    PAYMENT_SOURCE: {
      BASE: `${ARKON_BASE}/fhir/CodeSystem/payment-source`,
      CODES: {
        ELKE: 'ELKE',
        EPI: 'EPI',
        HOSPITAL: 'HOSPITAL',
      },
    },
    CHARGE_ITEM: {
      BASE: `${ARKON_BASE}/fhir/CodeSystem/charge-item-codes`,
      CODES: {
        EXAM_CHARGE: 'exam-charge',
        DEPOSIT: 'deposit',
      },
    },

    DELIVERY: {
      METHODS: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/delivery-methods`,
        CODES: {
          IN_PERSON: 'IN_PERSON',
          EMAIL: 'EMAIL',
          POST: 'POST',
          AUTHORIZED_PERSON: 'AUTHORIZED_PERSON',
        },
      },
      ANSWER: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/answer-delivery`,
      },
    },

    REPORTS: {
      INVESTIGATION_TYPE: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/cs-report-file-type-el`,
      },
      CATEGORY: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/cs-report-category-el`,
        CODES: {
          GENETIC: 'genetic',
          INVESTIGATION: 'investigation',
          BIOPSY: 'biopsy',
          PRENATAL_CHECK: 'prenatal-check',
          PRE_IMPLANTATION_CHECK: 'pre-implantation-check',
        },
      },
    },
    PREBIRTH_CHECKS: {
      BASE: `${ARKON_BASE}/fhir/CodeSystem/cs-prebirth-checks-el`,
    },
    DOCUMENT: {
      TYPE: {
        BASE: `${ARKON_BASE}/fhir/CodeSystem/document-type`,
        CODES: {
          REPORT_TEMPLATE: 'report-template',
        },
      },
    },
  },
  SD: {
    CLINICAL_INFORMATION: `${ARKON_BASE}/fhir/StructureDefinition/clinical-information`,
    GENE_REVIEW: `${ARKON_BASE}/fhir/StructureDefinition/geneReview`,
    PEDIGREE: {
      BASE: `${ARKON_BASE}/fhir/StructureDefinition/ainigma-pedigree-data`,
      CODE: 'ainigma-pedigree',
    },
    GENERAL_NOTES: `${ARKON_BASE}/fhir/StructureDefinition/patient-notes`,
    SPECIMEN_TYPE: `${ARKON_BASE}/fhir/StructureDefinition/specimen-type`,
    DELIVERY_PREFERENCE: `${ARKON_BASE}/fhir/StructureDefinition/delivery-preference`,
    EXTERNAL_ORGANIZATION_EXTENSION: `${ARKON_BASE}/fhir/StructureDefinition/external-organization`,
    DISCHARGE_INSTRUCTIONS: `${ARKON_BASE}/fhir/StructureDefinition/discharge-instructions`,
    CLINICAL_NOTES: `${ARKON_BASE}/fhir/StructureDefinition/clinical-notes`,
    LAB_ORDER_SUBSTATE: `${ARKON_BASE}/fhir/StructureDefinition/lab-order-substate`,
    DIAGNOSTIC_REPORT: {
      REVIEW_STATUS: `${ARKON_BASE}/fhir/StructureDefinition/diagnostic-report-review-status`,
      REFERENCES: `${ARKON_BASE}/fhir/StructureDefinition/diagnostic-report-references`,
      METHODOLOGIES: `${ARKON_BASE}/fhir/StructureDefinition/diagnostic-report-methodologies`,
    },
    MEDIA_SOURCE: `${ARKON_BASE}/fhir/StructureDefinition/media-source`,
    SPECIMEN_MEDIA: `${ARKON_BASE}/fhir/StructureDefinition/specimen-media`,
    CONSENT_STATUS_TAG: `${ARKON_BASE}/fhir/StructureDefinition/consent-status-tag`,
    PAYMENT_SOURCE: `${ARKON_BASE}/fhir/StructureDefinition/payment-source`,
    PAYMENT_RECEIPT: `${ARKON_BASE}/fhir/StructureDefinition/payment-receipt`,
    LINKED_ORDER: `${ARKON_BASE}/fhir/StructureDefinition/linked-order`,
    DISCOUNT: `${ARKON_BASE}/fhir/StructureDefinition/discount`,
    CHARGE_ITEM: `${ARKON_BASE}/fhir/StructureDefinition/charge-item`,
  },
  VS: {
    ALLERGY_NO_DRUG_EL_EN: `${ARKON_BASE}/fhir/ValueSet/vs-allergy-no-drug-el-en`,
    ALLERGY_MANIFESTATION_EL_EN: `${ARKON_BASE}/fhir/ValueSet/vs-allergy-manifestation-el-en`,
    ICD_10_EL: `${ARKON_BASE}/fhir/ValueSet/vs-icd-10-el`,
    ALLERGY_SEVERITY_EL_EN: `${ARKON_BASE}/fhir/ValueSet/vs-allergy-severity-el-en`,
    ALLERGY_INTOLERANCE_EL: `${ARKON_BASE}/fhir/ValueSet/vs-allergy-intolerance-clinical-el`,
    PROCEDURES_EL: `${ARKON_BASE}/fhir/ValueSet/vs-procedures-el`,
    SMOKING_STATUS_EL: `${ARKON_BASE}/fhir/ValueSet/vs-smoking-status-el-en`,
    SMOKING_TYPE_EL: `${ARKON_BASE}/fhir/ValueSet/vs-smoking-type-el-en`,
    ALCOHOL_STATUS_EL: `${ARKON_BASE}/fhir/ValueSet/vs-alcohol-status-el-en`,
    EXERCISE_STATUS_EL: `${ARKON_BASE}/fhir/ValueSet/vs-exercise-status-el-en`,
    ENCOUNTER_TYPE_EL: `${ARKON_BASE}/fhir/ValueSet/vs-encounter-type-el`,
    FILE_TYPE_EL: `${ARKON_BASE}/fhir/ValueSet/vs-file-type-el`,
    GENETIC_TESTS_EL: `${ARKON_BASE}/fhir/ValueSet/vs-genetic-tests-el-en`,
    SPECIMEN_TYPES_EL: `${ARKON_BASE}/fhir/ValueSet/vs-specimen-types-el`,
    REPORT_FILE_TYPE_EL: `${ARKON_BASE}/fhir/ValueSet/vs-report-file-type-el`,
    MEDICATIONS_EL: `${ARKON_BASE}/fhir/ValueSet/vs-medications-el`,
    MEDICATION_ROUTE_EL_EN: `${ARKON_BASE}/fhir/ValueSet/vs-medication-route-el-en`,
    THERAPEUTIC_INTERVENTIONS_EL: `${ARKON_BASE}/fhir/ValueSet/vs-therapeutic-intervention-el`,
    CONDITION_CLINICAL_EL: `${ARKON_BASE}/fhir/ValueSet/vs-condition-clinical-el`,
    PREBIRTH_CHECKS_EL: `${ARKON_BASE}/fhir/ValueSet/vs-prebirth-checks-el`,
  },
  CM: {
    BASE: `${ARKON_BASE}/fhir/ConceptMap`,
    VALUES: {
      GENETIC_TEST_TO_CONSENT: 'cm-genetic-test-to-consent',
      GENETIC_TEST_TO_TEMPLATE: 'cm-genetic-test-to-template',
    },
  },
  TEMPLATE: {
    ID: `${ARKON_BASE}/fhir/template-id`,
  },
  REPORT_CONTENT: {
    ID: `${ARKON_BASE}/fhir/report-content-id`,
  },
};

//------------------------------------------------------------------------------
// External code systems (SNOMED, LOINC, etc.)
export const EXTERNAL_CS = {
  SNOMED: {
    BASE: SYSTEMS.SNOMED,
    CODES: {
      BIRTH_WEIGHT: '364589006',
      BIRTH_LENGTH: '169886007',
      BIRTH_HEAD_CIRCUMFERENCE: '169876006',
      GESTATIONAL_AGE: '603148',
      DIAGNOSTIC_REPORT_REVIEW: '117617002',
      LABORATORY_RESULT_REVIEW: '168537006',
      LABORATORY_PROCEDURE: '108252007',
      ALLERGY_SEVERITY_MILD: '255604002',
      ALLERGY_SEVERITY_MILD_TO_MODERATE: '371923003',
      ALLERGY_SEVERITY_MODERATE: '6736007',
      ALLERGY_SEVERITY_MODERATE_TO_SEVERE: '371924009',
      ALLERGY_SEVERITY_SEVERE: '24484000',
      ALLERGY_SEVERITY_LIFE_THREATENING: '442452003',
      NORMAL_RESULT: '280413001',
      ABNORMAL_RESULT: '280415008',
      NON_SPECIFIC_RESULT: '280416009',
    },
  },
  LOINC: {
    BASE: SYSTEMS.LOINC,
    CODES: {
      BODY_WEIGHT: '29463-7',
      BODY_HEIGHT: '8302-2',
      HEAD_CIRCUMFERENCE: '9843-4',
      BMI: '39156-5',
      AUTHORIZATION_DOCUMENT: '64292-6',
      CONSENT_DOCUMENT: '59284-0',
      ORDER_DOCUMENT: '93037-0',
      // Vital signs for Physical Examination
      BLOOD_PRESSURE_PANEL: '85354-9',
      BLOOD_PRESSURE_SYSTOLIC: '8480-6',
      BLOOD_PRESSURE_DIASTOLIC: '8462-4',
      HEART_RATE: '8867-4',
      RESPIRATORY_RATE: '9279-1',
      BODY_TEMPERATURE: '8310-5',
      OXYGEN_SATURATION: '2708-6',
    },
  },
};

// -----------------------------------------------------------------------------
// Birth Measurement Codes
export const BIRTH_MEASUREMENT_CODES = {
  WEIGHT: {
    loinc: EXTERNAL_CS.LOINC.CODES.BODY_WEIGHT,
    snomed: EXTERNAL_CS.SNOMED.CODES.BIRTH_WEIGHT,
    loincDisplay: 'Body Weight',
    snomedDisplay: 'Birth Weight',
    displayEl: 'Βάρος Σώματος',
    unit: 'g',
  },
  LENGTH: {
    loinc: EXTERNAL_CS.LOINC.CODES.BODY_HEIGHT,
    snomed: EXTERNAL_CS.SNOMED.CODES.BIRTH_LENGTH,
    loincDisplay: 'Body Height',
    snomedDisplay: 'Birth Length',
    displayEl: 'Μήκος Σώματος',
    unit: 'cm',
  },
  HEAD_CIRCUMFERENCE: {
    loinc: EXTERNAL_CS.LOINC.CODES.HEAD_CIRCUMFERENCE,
    snomed: EXTERNAL_CS.SNOMED.CODES.BIRTH_HEAD_CIRCUMFERENCE,
    loincDisplay: 'Head Circumference',
    snomedDisplay: 'Birth Head Circumference',
    displayEl: 'Περίμετρος Κεφαλής',
    unit: 'cm',
  },
} as const;

//------------------------------------------------------------------------------
export const IDENTIFIERS = {
  AMKA: 'https://www.amka.gr/',
  SPECIMEN_EXTERNAL_ID: `${ARKON_BASE}/fhir/identifier/specimen-external-id`,
};

// -----------------------------------------------------------------------------
// extension urls - TODO!! - check existing questionnaires before making changes
export const RENDERING_STYLE_EXTENSION_URL = `${ARKON_BASE}/fhir/StructureDefinition/rendering-style`;
export const QUESTIONNAIRE_Q_DESC_EXTENSION_URL = `${ARKON_BASE}/fhir/StructureDefinition/question-description`;
export const QUESTIONNAIRE_Q_PLACEHOLDER_EXTENSION_URL = `${ARKON_BASE}/fhir/StructureDefinition/question-placeholder`;
export const QUESTIONNAIRE_API_EXT_EXTENSION_URL = `${ARKON_BASE}/questionnaireItem/apiExt`;
export const QUESTIONNAIRE_API_URL_EXTENSION_URL = `${ARKON_BASE}/questionnaireItem/apiUrl`;
export const QUESTIONNAIRE_SYSTEM_EXTENSION_URL = `${ARKON_BASE}/questionnaireItem/system`;
export const QUESTIONNAIRE_PREFIX_EXTENSION_URL = `${ARKON_BASE}/questionnaireItem/prefix`;
export const QUESTIONNAIRE_IS_MULTI_EXTENSION_URL = `${ARKON_BASE}/questionnaireItem/isMulti`;
export const REPORT_MODALITY_EXTENSION_URL = `${ARKON_BASE}/fhir/StructureDefinition/report-investigation-modality`;
export const ALLERGY_INTOLERANCE_SEVERITY_EXTENSION_URL = `${ARKON_BASE}/fhir/allergyIntolerance/full-severity-coding`;
export const PRENATAL_CHECK_REASON_EXTENSION_URL = `${ARKON_BASE}/fhir/StructureDefinition/prenatal-check-reason`;
export const PRENATAL_CHECK_TEST_TYPE_EXTENSION_URL = `${ARKON_BASE}/fhir/StructureDefinition/prenatal-check-test-type`;
export const TIPTAP_CONTENT_EXTENSION_URL = `${ARKON_BASE}/fhir/StructureDefinition/tiptap-report-content`;
export const TEMPLATE_ID_EXTENSION_URL = `${ARKON_BASE}/fhir/StructureDefinition/report-template-id`;
export const TEMPLATE_VERSION_EXTENSION_URL = `${ARKON_BASE}/fhir/StructureDefinition/report-template-version`;
